<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">
        {{ $t('invoiceClass.pageTitle') }}
      </h1>
      <ts-breadcrumb
        :items="[
          { text: $t('toHomePage'), href: '/' },
          { text: $t('invoiceClass.pageTitle'), href: '/invoice-classes' },
          { text: $t('create'), active: true }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <ts-loading-banner :loading="loading">
          <div class="row">
            <div class="col-md-12">
              <h1
                class="
                ts-rounded ts-font-semibold ts-uppercase ts-text-base 
                ts-bg-gradient-to-l ts-from-transparent ts-to-gray-200 ts-py-1 ts-px-2 
                ts-text-blue-600"
              >
                {{ $t('invoiceClass.student') }}
              </h1>
              <div class="ts-border ts-p-4 ts-rounded-md ts-mt-2">
                <input
                  v-model="searchStudent"
                  type="text"
                  class="ts-border ts-rounded ts-outline-none ts-px-3 ts-py-2 ts-w-full"
                  placeholder="Search|Name,ID Card, Invoice Number"
                  @input="onSearchStudent"
                />
                <div
                  class="ts-mt-3 ts-rounded ts-bg-blue-200 ts-py-1 ts-px-3 ts-relative transition-all duration-300"
                  v-if="selectInvoiceText"
                >
                  <p v-html="selectInvoiceText"></p>
                  <button
                    class="ts-absolute ts-top-0 ts-mt-1 ts-right-0 ts-text-red-600 ts-mr-1"
                    @click.prevent="onClearInvoiceSelect"
                  >
                    <i class="fas fa-times-circle ts-text-base"></i>
                  </button>
                </div>
                <div
                  class="ts-w-full ts-mt-3"
                  style="min-height: 200px; max-height: 250px; overflow-y: scroll;"
                >
                  <table class="ts-w-full">
                    <thead>
                      <tr>
                        <th
                          class="ts-uppercase ts-py-1 ts-px-2 ts-border ts-w-3 ts-bg-gray-100"
                          rowspan="2"
                        >
                          {{ $t('no') }}
                        </th>
                        <th
                          class="ts-uppercase ts-text-center ts-px-2 ts-py-1 ts-border ts-text-blue-600 ts-bg-gray-100"
                          colspan="3"
                        >
                          {{ $t('invoiceClass.studentInfo') }}
                        </th>
                        <th
                          class="ts-uppercase ts-text-center ts-px-2 ts-py-1 ts-border ts-text-blue-600 ts-bg-gray-100"
                          colspan="3"
                        >
                          {{ $t('invoiceClass.invoice') }}
                        </th>
                        <th
                          class="ts-uppercase ts-text-center ts-px-2 ts-py-1 ts-border ts-text-blue-600 ts-bg-gray-100"
                          colspan="2"
                        >
                          {{ $t('invoiceClass.class') }}
                        </th>
                      </tr>
                      <tr class="ts-border-b-2 ts-border-black">
                        <th
                          class="ts-uppercase ts-py-1 ts-px-2 ts-bg-gray-100 ts-border ts-w-32"
                        >
                          {{ $t('invoiceClass.cardNumber') }}
                        </th>
                        <th
                          class="ts-uppercase ts-py-1 ts-px-2 ts-bg-gray-100 ts-border"
                        >
                          {{ $t('invoiceClass.studentName') }}
                        </th>
                        <th
                          class="ts-uppercase ts-py-1 ts-px-2 ts-bg-gray-100 ts-border"
                        >
                          {{ $t('invoiceClass.gender') }}
                        </th>
                        <th
                          class="ts-uppercase ts-py-1 ts-px-2 ts-bg-gray-100 ts-border"
                        >
                          {{ $t('invoiceClass.invNumber') }}
                        </th>
                        <th
                          class="ts-uppercase ts-py-1 ts-px-2 ts-bg-gray-100 ts-border"
                        >
                          {{ $t('invoiceClass.invDate') }}
                        </th>
                        <th
                          class="ts-uppercase ts-py-1 ts-px-2 ts-bg-gray-100 ts-border"
                        >
                          {{ $t('invoiceClass.totalAmount') }}
                        </th>
                        <th
                          class="ts-uppercase ts-py-1 ts-px-2 ts-bg-gray-100 ts-border"
                        >
                          {{ $t('invoiceClass.class') }}
                        </th>
                        <th
                          class="ts-uppercase ts-py-1 ts-px-2 ts-bg-gray-100 ts-border"
                        >
                          {{ $t('invoiceClass.description') }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(student, index) in students"
                        :key="index"
                        class="ts-border-b hover:ts-bg-gray-200 ts-cursor-pointer"
                        @click="onSelectedInvoice(student)"
                      >
                        <td class=" ts-py-1 ts-px-2 ts-text-center">
                          {{ index + 1 }}
                        </td>
                        <td class=" ts-py-1 ts-px-2">
                          {{ student.card_number }}
                        </td>
                        <td class=" ts-py-1 ts-px-2">
                          {{
                            student.first_name_en + ' ' + student.last_name_en
                          }}
                        </td>
                        <td class=" ts-py-1 ts-px-2">
                          {{ student.gender }}
                        </td>
                        <td class=" ts-py-1 ts-px-2">
                          {{ student.inv_number }}
                        </td>
                        <td class=" ts-py-1 ts-px-2">
                          {{ student.inv_date }}
                        </td>
                        <td class=" ts-py-1 ts-px-2">
                          {{ formatNumber(student.total_amount) }}
                        </td>
                        <td class=" ts-py-1 ts-px-2">
                          {{ student.class_name }}
                        </td>
                        <td class=" ts-py-1 ts-px-2">
                          {{ student.class_desc }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div
                  class="ts-text-red-600 ts-mt-3"
                  v-if="errors.has('student_id')"
                >
                  {{ errors.first('student_id') }}
                </div>
              </div>
            </div>
            <div class="col-md-12 ts-mt-3">
              <h1
                class="ts-rounded ts-font-semibold ts-uppercase ts-text-base ts-bg-gradient-to-l ts-from-transparent ts-to-gray-200 ts-py-1 ts-px-2 ts-text-blue-600"
              >
                {{ $t('invoiceClass.class') }}
              </h1>
              <div class="ts-border ts-p-4 ts-rounded-md ts-mt-2">
                <div class="row">
                  <div class="col-md-3">
                    <label>{{ $t('invoiceClass.acYear') }}</label>
                    <a-select
                      v-model="searchOption.ac_year_id"
                      style="width:100%"
                      :allowClear="true"
                      :showSearch="true"
                      :options="acYears"
                      :filterOption="selectSearch"
                      optionFilterProp="children"
                      @change="fetchTerm"
                    />
                  </div>
                  <div class="col-md-3">
                    <label>{{ $t('invoiceClass.mainProgram') }}</label>
                    <a-select
                      v-model="searchOption.acp_id"
                      style="width:100%"
                      :allowClear="true"
                      :showSearch="true"
                      :options="mainProgram"
                      :filterOption="selectSearch"
                      optionFilterProp="children"
                      @change="fetSubProgram"
                    />
                  </div>
                  <div class="col-md-3">
                    <label>{{ $t('invoiceClass.subProgram') }}</label>
                    <a-select
                      v-model="searchOption.acps_id"
                      style="width:100%"
                      :allowClear="true"
                      :showSearch="true"
                      :options="subProgram"
                      :filterOption="selectSearch"
                      optionFilterProp="children"
                      @change="fetchTerm"
                    />
                  </div>
                  <div class="col-md-3">
                    <label>{{ $t('invoiceClass.academicTerm') }}</label>
                    <a-select
                      v-model="searchOption.ac_term_id"
                      style="width:100%"
                      :allowClear="true"
                      :showSearch="true"
                      :options="academicTerm"
                      :filterOption="selectSearch"
                      optionFilterProp="children"
                    />
                  </div>
                </div>
                <div class="row ts-mt-3">
                  <div class="col-md-12">
                    <div
                      class="ts-overflow-hidden ts-rounded ts-flex ts-space-x-3"
                    >
                      <input
                        v-model.trim="searchOption.search"
                        type="text"
                        class="ts-w-full ts-border ts-rounded ts-outline-none"
                        placeholder="Search| Class Name"
                        style="padding: .4rem 1rem;"
                      />
                      <ts-button color="info" @click.prevent="onSearchClass">{{
                        $t('search')
                      }}</ts-button>
                    </div>
                  </div>
                </div>
                <div
                  class="ts-mt-3 ts-rounded ts-bg-blue-200 ts-py-1 ts-px-3 ts-relative"
                  v-show="selectClassText"
                >
                  <p v-html="selectClassText"></p>
                  <button
                    class="ts-absolute ts-top-0 ts-mt-1 ts-right-0 ts-text-red-600 ts-mr-1"
                    @click="onClearClassSelect"
                  >
                    <i class="fas fa-times-circle ts-text-base"></i>
                  </button>
                </div>
                <div class="row ts-mt-3">
                  <div
                    class="ts-px-3 ts-w-full"
                    style="min-height: 200px; max-height: 250px; overflow-y: scroll;"
                  >
                    <table class="ts-w-full">
                      <thead>
                        <tr class="ts-border-b-2 ts-border-black">
                          <th
                            class="ts-bg-gray-100 ts-border ts-py-1 ts-px-2 ts-sticky ts-top-0 ts-z-10 ts-uppercase"
                          >
                            {{ $t('no') }}
                          </th>
                          <th
                            class="ts-bg-gray-100 ts-border ts-py-1 ts-px-2 ts-sticky ts-top-0 ts-z-10 ts-uppercase"
                          >
                            {{ $t('invoiceClass.class') }}
                          </th>
                          <th
                            class="ts-bg-gray-100 ts-border ts-py-1 ts-px-2 ts-sticky ts-top-0 ts-z-10 ts-uppercase"
                          >
                            {{ $t('invoiceClass.description') }}
                          </th>
                          <th
                            class="ts-bg-gray-100 ts-border ts-py-1 ts-px-2 ts-sticky ts-top-0 ts-z-10 ts-uppercase"
                          >
                            {{ $t('invoiceClass.acYear') }}
                          </th>
                          <th
                            class="ts-bg-gray-100 ts-border ts-py-1 ts-px-2 ts-sticky ts-top-0 ts-z-10 ts-uppercase"
                          >
                            {{ $t('invoiceClass.mainProgram') }}
                          </th>
                          <th
                            class="ts-bg-gray-100 ts-border ts-py-1 ts-px-2 ts-sticky ts-top-0 ts-z-10 ts-uppercase"
                          >
                            {{ $t('invoiceClass.subProgram') }}
                          </th>
                          <th
                            class="ts-bg-gray-100 ts-border ts-py-1 ts-px-2 ts-sticky ts-top-0 ts-z-10 ts-uppercase"
                          >
                            {{ $t('invoiceClass.grade') }}
                          </th>
                          <th
                            class="ts-bg-gray-100 ts-border ts-py-1 ts-px-2 ts-sticky ts-top-0 ts-z-10 ts-uppercase"
                          >
                            {{ $t('invoiceClass.academicTerm') }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(cl, index) in classes"
                          :key="index"
                          @click="onSelectedClass(cl)"
                          class="ts-border-b hover:ts-bg-gray-200 ts-cursor-pointer"
                        >
                          <td class="ts-border-b ts-py-1 ts-px-2">
                            {{ index + 1 }}
                          </td>
                          <td class="ts-border-b ts-py-1 ts-px-2">
                            {{ cl.class_name }}
                          </td>
                          <td class="ts-border-b ts-py-1 ts-px-2">
                            {{ cl.class_desc }}
                          </td>
                          <td class="ts-border-b ts-py-1 ts-px-2">
                            {{ cl.ac_year }}
                          </td>
                          <td class="ts-border-b ts-py-1 ts-px-2">
                            {{ cl.acp_name_foreign }}
                          </td>
                          <td class="ts-border-b ts-py-1 ts-px-2">
                            {{ cl.acps_name_foreign }}
                          </td>
                          <td class="ts-border-b ts-py-1 ts-px-2">
                            {{ cl.grade_name_foreign }}
                          </td>
                          <td class="ts-border-b ts-py-1 ts-px-2">
                            {{ cl.term_name }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div
                    class="ts-text-red-600 ts-mt-3"
                    v-if="errors.has('class_id')"
                  >
                    {{ errors.first('class_id') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row ts-mt-3">
            <div class="col-md-12 text-right">
              <ts-button
                @click.prevent="$router.push({ name: 'invoice-classes' })"
                >{{ $t('cancel') }}</ts-button
              >
              <ts-button color="primary" outline class="ml-2"
                @click.prevent="onSaveAndAddNew"
              >{{
                $t('save_add_new')
              }}</ts-button>
              <ts-button @click.prevent="onSave" color="primary" class="ml-2">{{
                $t('save')
              }}</ts-button>
            </div>
          </div>
        </ts-loading-banner>
      </ts-card-wrapper>
    </ts-card>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { debounce } from 'lodash'
import { Errors } from 'form-backend-validation'

export default {
  name: 'invoiceClass',
  data() {
    return {
      loading: false,
      searchStudent: '',
      students: [],
      acYears: [],
      mainProgram: [],
      subProgram: [],
      academicTerm: [],
      classes: [],
      selectedInvoice: {},
      selectedClass: {},
      searchOption: {
        acp_id: '',
        acps_id: '',
        ac_year_id: '',
        ac_term_id: '',
        search: ''
      },
      model: {
        inv_id: '',
        student_id: '',
        class_id: ''
      },
      errors: new Errors()
    }
  },
  computed: {
    formatNumber: () => number => {
      if (number !== undefined) {
        return new Intl.NumberFormat('en-IN', {
          maximumSignificantDigits: 3
        }).format(number)
      }

      return 0
    },
    selectInvoiceText() {
      if (Object.keys(this.selectedInvoice).length) {
        return `
        <b>CARD NUMBER:</b> ${this.selectedInvoice.card_number}, 
        <b>FULL NAME:</b> ${this.selectedInvoice.first_name_en} ${this.selectedInvoice.last_name_en}, 
        <b>GENDER:</b> ${this.selectedInvoice.gender},
        <b>INVOICE NUMBER:</b> ${this.selectedInvoice.inv_number}
      `
      }
      return undefined
    },
    selectClassText() {
      if (Object.keys(this.selectedClass).length) {
        return `
        <b>ACADEMIC YEAR:</b> ${this.selectedClass.ac_year}, 
        <b>ACADEMIC TERM:</b> ${this.selectedClass.ac_year}, 
        <b>CLASS NAME:</b> ${this.selectedClass.term_name}, 
        <b>CLASS DESC:</b> ${this.selectedClass.class_desc},
      `
      }
      return undefined
    },
    is_external() {
      return this.$store.state.authUser.is_external
    },
  },
  methods: {
    ...mapActions('invoiceClass', ['store', 'formViewModel']),
    getStudent() {
      this.loading = true
      this.formViewModel({
        fnName: 'students,academicYear,mainProgram,academicClass',
        search: this.searchStudent
      })
        .then(result => {
          this.students = result.data.students
          this.acYears = result.data.academicYear.map(el => ({
            value: el.ac_year_id,
            label: el.ac_year
          }))
          this.mainProgram = result.data.mainProgram.map(el => ({
            value: el.acp_id,
            label: el.acp_name_foreign
          }))
          this.classes = result.data.academicClass
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => (this.loading = false))
    },
    onSearchStudent: debounce(function() {
      this.getStudent()
    }, 800),
    selectSearch(input, option) {
      return (
        option.componentOptions.children[0].text
          .toLowerCase()
          .indexOf(input.toLowerCase()) >= 0
      )
    },
    fetSubProgram(value, option) {
      this.loading = true
      this.formViewModel({
        fnName: 'subProgram',
        acp_id: value
      })
        .then(result => {
          this.subProgram = result.data.subProgram.map(el => ({
            value: el.acps_id,
            label: el.acps_name_foreign
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => (this.loading = false))
    },
    fetchTerm() {
      this.loading = true
      this.searchOption.ac_term_id = ''
      // this.searchOption.acps_id = ''
      this.formViewModel({
        fnName: 'term',
        ac_year_id: this.searchOption.ac_year_id,
        acps_id: this.searchOption.acps_id
      })
        .then(result => {
          this.academicTerm = result.data.term.map(el => ({
            value: el.ac_term_id,
            label: el.term_name
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => (this.loading = false))
    },
    onSearchClass() {
      this.loading = true
      this.formViewModel({
        fnName: 'academicClass',
        ...this.searchOption
      })
        .then(result => {
          this.classes = result.data.academicClass
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => (this.loading = false))
    },
    onSelectedInvoice(row) {
      this.selectedInvoice = row
      this.model.inv_id = row.inv_id
      this.model.student_id = row.student_id
    },
    onClearInvoiceSelect() {
      this.selectedInvoice = {}
      this.model.inv_id = ''
      this.model.student_id = ''
    },
    onSelectedClass(row) {
      this.selectedClass = row
      this.model.class_id = row.class_id
    },
    onClearClassSelect() {
      this.selectedClass = {}
      this.model.class_id = ''
    },
    onSave() {
      this.loading = true
      this.store(this.model)
        .then(response => {
          this.$toasted.success(response.message)
          this.$router.push({ name: 'invoice-classes' })
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.loading = false
        })
    },
    onSaveAndAddNew() {
      this.loading = true
      this.store(this.model)
        .then(response => {
          this.$toasted.success(response.message)
          this.clearInput()
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.loading = false
        })
    },
    clearInput() {
      this.model.inv_id = ''
      this.model.student_id = ''
      this.model.class_id = ''
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.getStudent()
    })
  },
  watch: {
    is_external: debounce(function() {
      this.getStudent()
    }, 500),
  },
}
</script>
